/**
 * Columns Folder Loader - Loads columns for a table subfolder
 */

import {
  DatabaseExplorerNode,
  MessageTreeNode,
  ColumnsFolderNode,
  createColumnNode,
  ColumnNodeMetadata,
} from '../../explorer/models/DatabaseTreeModels';
import {
  createTableSubfolderEmptyNode,
  createTableSubfolderErrorNode,
} from './objectFolderUtils';
import { getColumns, ColumnInfo } from './columnsService';
import { getEngineForConnection } from './tableExpansion';

export interface LoadColumnsFolderOptions {
  node: ColumnsFolderNode;
  updateNode: (nodeId: string, updates: Partial<DatabaseExplorerNode>) => void;
  forceReload?: boolean;
}

/**
 * Loads the columns for a table when the Columns folder is expanded
 */
export async function loadColumnsFolder({
  node,
  updateNode,
  forceReload = false,
}: LoadColumnsFolderOptions) {
  // Don't reload if already has children (unless it's a loading/error message or force reload)
  if (!forceReload && node.children && node.children.length > 0) {
    const firstChild = node.children[0];
    if (firstChild && !['message', 'error'].includes(firstChild.type)) {
      return;
    }
  }

  const { connectionId, database, schema, tableName } = node.metadata;

  // Ensure database is defined
  if (!database) {
    updateNode(node.id, {
      children: [
        createTableSubfolderErrorNode(
          connectionId,
          '',
          schema,
          tableName,
          'columns-folder',
          node.id,
          'Database not specified'
        ),
      ],
      isLoading: false,
    });
    return;
  }

  // Show loading state
  updateNode(node.id, {
    children: [
      {
        id: `loading-${connectionId}-${database}-${schema}-${tableName}-columns`,
        parentId: node.id,
        name: 'Loading columns...',
        type: 'message',
        canHaveChildren: false,
        children: [],
        isExpanded: false,
        icon: 'loading',
        metadata: { connectionId, database },
      } as MessageTreeNode,
    ],
    isLoading: true,
  });

  try {
    const engine = getEngineForConnection(connectionId);
    const result = await getColumns(connectionId, database, schema, tableName, engine);

    if (result.error) {
      updateNode(node.id, {
        children: [
          createTableSubfolderErrorNode(
            connectionId,
            database,
            schema,
            tableName,
            'columns-folder',
            node.id,
            result.error
          ),
        ],
        isLoading: false,
      });
      return;
    }

    if (result.columns.length === 0) {
      updateNode(node.id, {
        children: [
          createTableSubfolderEmptyNode(
            connectionId,
            database,
            schema,
            tableName,
            'columns-folder',
            node.id
          ),
        ],
        isLoading: false,
      });
      return;
    }

    // Create column nodes
    const columnNodes = result.columns.map((col: ColumnInfo) => {
      const metadata: ColumnNodeMetadata = {
        connectionId,
        database,
        schema,
        tableName,
        columnName: col.name,
        dataType: col.dataType,
        maxLength: col.maxLength ?? undefined,
        precision: col.precision ?? undefined,
        scale: col.scale ?? undefined,
        isNullable: col.isNullable,
        isPrimaryKey: col.isPrimaryKey,
        isIdentity: col.isIdentity,
        defaultValue: col.defaultValue ?? undefined,
      };
      return createColumnNode(metadata, node.id);
    });

    updateNode(node.id, {
      children: columnNodes,
      isLoading: false,
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to load columns';
    updateNode(node.id, {
      children: [
        createTableSubfolderErrorNode(
          connectionId,
          database,
          schema,
          tableName,
          'columns-folder',
          node.id,
          message
        ),
      ],
      isLoading: false,
    });
  }
}
